import React, { useEffect, useState, useRef } from 'react';
import DatePicker from 'portaltransparencia-common/components/DatePicker.react';
import Panel from 'portaltransparencia-common/components/Panel.react.js';
import AutoComplete from 'portaltransparencia-common/components/AutoComplete.react';
import { Col, Row } from 'react-bootstrap';
import GraficoBarra from './GraficoBarra';
import GraficoLinha from './GraficoLinha';
import TabelaDrilldown from './TabelaDrilldown';
import {
  montarCubeQueryPadrao,
  montarCubeQueryDrilldown
} from './CubeQueryUtils';

const API_BASE = '/portaltransparencia-api/api/visao-custo-cube';
const END_POINT_PORTAL = `${API_BASE}/portal-transparencia-visao-custo-cube`;

const fetchOptions = async (url, idsSelecionados = [], filtros = []) => {
  const query = [];

  if (idsSelecionados.length) {
    query.push(`id=out=(${idsSelecionados.join(',')})`);
  }

  if (filtros.length) {
    query.push(...filtros);
  }

  const finalUrl = `${url}?search=${query.join('&')}`;

  const response = await fetch(finalUrl);
  if (!response.ok) throw new Error('Erro ao carregar dados');

  const value = await response.json();

  return { value };
};

const periodoOptions = [
  { codigo: 'month', descricao: 'Mensal' },
  { codigo: 'quarter', descricao: 'Trimestral' },
  { codigo: 'year', descricao: 'Anual' }
];

const ParametrosVisaoCusto = ({ centroCusto: initialCentroCusto }) => {
  const [centroCusto, setCentroCusto] = useState(initialCentroCusto);

  const dto = centroCusto?.visaoCustoCubeDTO?.visaoCustoCubeItem || {};

  const [id] = useState(centroCusto?.visaoCustoCubeDTO?.id || '');
  const [dataDe, setDataDe] = useState(dto.dataInicio || '');
  const [dataAte, setDataAte] = useState(dto.dataFim || '');
  const [periodo, setPeriodo] = useState(dto.periodo || 'Nenhum');
  const [tipoGrafico, setTipoGrafico] = useState(dto.chartType || 'Barra');
  const [centrosSelecionados, setCentrosSelecionados] = useState(
    dto.idsCentroCustoDetalhados || []
  );
  const [objetosSelecionados, setObjetosSelecionados] = useState(
    dto.idsObjetoCustoDetalhados || []
  );
  const [indicadorSelecionado, setIndicadorSelecionado] = useState(
    dto.indicadorOficial || null
  );
  const [divisorSelecionado, setDivisorSelecionado] = useState(
    dto.divisor || null
  );
  const [medida, setMedida] = useState(
    dto.medida || 'DataBlendingVisao.sumValorTotal'
  );

  const jaPesquisouRef = useRef(false);

  useEffect(() => {
    if (
      centroCusto?.visaoCustoCubeDTO?.tipoVisualizacao?.codigo === 1 &&
      !jaPesquisouRef.current
    ) {
      handlePesquisar();
      jaPesquisouRef.current = true;
    }
  }, [centroCusto]);

  const loadCentros = loadCentrosFunction(centrosSelecionados);

  const loadObjetos = loadObjetosFunction(objetosSelecionados);

  const loadIndicadores = loadIndicadoresFunction();

  const loadDivisores = loadDivisoresFunction();

  const handleSelecionarCentro = handleSelecionarCentroFunction(
    centrosSelecionados,
    setCentrosSelecionados
  );

  const handleRemoverCentro = handleRemoverCentroFunction(
    setCentrosSelecionados,
    centrosSelecionados
  );

  const handleSelecionarObjeto = handleSelecionarObjetoFunction(
    objetosSelecionados,
    setObjetosSelecionados
  );

  const handleRemoverObjeto = handleRemoverObjetoFunction(
    setObjetosSelecionados,
    objetosSelecionados
  );

  const handleSelecionarIndicador = handleSelecionarIndicadorFunction(
    setIndicadorSelecionado
  );

  const handleSelecionarDivisor = handleSelecionarDivisorFunction(
    setDivisorSelecionado
  );

  const getTimeKey = getTimeKeyFunction();

  const handlePesquisar = handlePesquisarFunction(
    centroCusto,
    centrosSelecionados,
    objetosSelecionados,
    dataDe,
    dataAte,
    medida,
    divisorSelecionado,
    periodo,
    indicadorSelecionado,
    id,
    tipoGrafico,
    setCentroCusto
  );

  const handleExpandDrilldown = handleExpandDrilldownFunction(
    centrosSelecionados,
    objetosSelecionados,
    dataDe,
    dataAte,
    medida,
    indicadorSelecionado,
    divisorSelecionado
  );

  return (
    <Panel
      title={`Visão de Custo - ${centroCusto.visaoCustoCubeDTO.descricao}`}
    >
      <>
        <Panel
          title={`Parâmetros da Visão de Custo - ${centroCusto.visaoCustoCubeDTO.descricao}`}
          mobileClose
          isForm
          collapsed
        >
          <form>
            <Row>
              <Col xs={12} sm={6} md={3}>
                <DatePicker
                  id="dataDe"
                  name="dataDe"
                  label="De"
                  onChange={e => setDataDe(e.target.value)}
                  value={dataDe}
                />
              </Col>
              <Col xs={12} sm={6} md={3}>
                <DatePicker
                  id="dataAte"
                  name="dataAte"
                  label="Até"
                  onChange={e => setDataAte(e.target.value)}
                  value={dataAte}
                />
              </Col>
              {centroCusto?.visaoCustoCubeDTO.tipoVisualizacao.codigo == 2 && (
                <>
                  <Col xs={12} sm={6} md={3}>
                    <div className={'form-group'}>
                      <label className="label">Período</label>
                      <select
                        className="form-control control-label"
                        value={periodo}
                        onChange={e => setPeriodo(e.target.value)}
                      >
                        <option value="">Nenhum</option>
                        {periodoOptions.map(option => (
                          <option key={option.codigo} value={option.codigo}>
                            {option.descricao}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={3}>
                    <div className={'form-group'}>
                      <label className="label">Tipo do Gráfico</label>
                      <select
                        className="form-control control-label"
                        value={tipoGrafico}
                        onChange={e => setTipoGrafico(e.target.value)}
                      >
                        <option key="bar" value="bar">
                          Barra
                        </option>
                        <option key="line" value="line">
                          Linha
                        </option>
                      </select>
                    </div>
                  </Col>
                </>
              )}
            </Row>

            <Row style={{ marginTop: '20px' }}>
              <Col xs={12}>
                <label className="label">Centros de Custo Selecionados:</label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    maxHeight: '72px',
                    overflowY: 'auto',
                    marginBottom: '8px',
                    paddingRight: '4px'
                  }}
                >
                  {centrosSelecionados.map(c => (
                    <span
                      key={c?.id}
                      onClick={() => handleRemoverCentro(c.id)}
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'green',
                        color: 'white',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {c?.descricao} ✖
                    </span>
                  ))}
                </div>

                <AutoComplete
                  label="Centro de Custo"
                  name="centro"
                  onLoad={loadCentros}
                  onChange={handleSelecionarCentro}
                  filterFields={['id', 'descricao']}
                  value={{}}
                  key={`centro-${centrosSelecionados.length}`}
                  minimumInput={2}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: '20px' }}>
              <Col xs={12}>
                <label className="label">Objetos de Custo Selecionados:</label>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    maxHeight: '72px',
                    overflowY: 'auto',
                    marginBottom: '8px',
                    paddingRight: '4px'
                  }}
                >
                  {objetosSelecionados.map(c => (
                    <span
                      key={c?.id}
                      onClick={() => handleRemoverObjeto(c.id)}
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'green',
                        color: 'white',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {c?.descricao} ✖
                    </span>
                  ))}
                </div>
                <AutoComplete
                  label="Objeto de Custo"
                  name="objeto"
                  onLoad={loadObjetos}
                  onChange={handleSelecionarObjeto}
                  filterFields={['id', 'descricao']}
                  value={{}}
                  key={`objeto-${objetosSelecionados.length}`}
                  minimumInput={2}
                  clearable
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6}>
                <AutoComplete
                  label="Indicador Oficial"
                  name="indicador"
                  onLoad={loadIndicadores}
                  onChange={handleSelecionarIndicador}
                  filterFields={['descricao', 'id']}
                  value={indicadorSelecionado}
                  key={`indicador-${indicadorSelecionado}`}
                  minimumInput={2}
                  clearable
                />
              </Col>
              <Col xs={12} sm={6}>
                <div className={'form-group'}>
                  <label className="label">Medidas</label>
                  <select
                    className="form-control control-label"
                    value={medida}
                    onChange={e => setMedida(e.target.value)}
                  >
                    <option value="DataBlendingVisao.sumValorTotal">
                      Valor Total
                    </option>
                    <option value="DataBlendingVisao.CustoPorDivisor">
                      Custo por Divisor
                    </option>
                  </select>
                </div>
              </Col>
              {centroCusto?.visaoCustoCubeDTO.tipoVisualizacao.codigo == 1 && (
                <Col xs={12} sm={6}>
                  <AutoComplete
                    label="Divisor"
                    name="divisor"
                    onLoad={loadDivisores}
                    onChange={handleSelecionarDivisor}
                    filterFields={['descricao', 'id']}
                    value={divisorSelecionado}
                    key={`divisor-${divisorSelecionado}`}
                    minimumInput={2}
                    clearable
                  />
                </Col>
              )}
            </Row>

            <Row>
              <Col xs={12} style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn neutral"
                  onClick={handlePesquisar}
                >
                  Pesquisar
                </button>
              </Col>
            </Row>
          </form>
        </Panel>
      </>

      {centroCusto && (
        <div style={{ marginTop: '30px' }}>
          <h5>{centroCusto.visaoCustoCubeDTO.descricao}</h5>

          {centroCusto.cubeJsResponse?.results?.[0] && (
            <>
              {centroCusto.visaoCustoCubeDTO.tipoVisualizacao.codigo == 2 &&
              tipoGrafico === 'line' ? (
                <GraficoLinha
                  dados={centroCusto.cubeJsResponse.results[0].data}
                  xKey={getTimeKey(periodo)}
                  descricaoKeys={[
                    'CentroCusto.descricao',
                    'ObjetoCusto.descricao'
                  ]}
                  valorKey="DataBlendingVisao.sumValorTotal"
                />
              ) : centroCusto.visaoCustoCubeDTO.tipoVisualizacao.codigo == 2 &&
                tipoGrafico === 'bar' ? (
                <GraficoBarra
                  dados={centroCusto.cubeJsResponse.results[0].data}
                  xKey={getTimeKey(periodo)}
                  descricaoKeys={[
                    'CentroCusto.descricao',
                    'ObjetoCusto.descricao'
                  ]}
                  valorKey="DataBlendingVisao.sumValorTotal"
                />
              ) : centroCusto.visaoCustoCubeDTO.tipoVisualizacao.codigo == 1 ? (
                <TabelaDrilldown
                  dados={centroCusto.cubeJsResponse?.results?.[0]?.data || []}
                  medida={medida}
                  objetosSelecionados={objetosSelecionados}
                  onExpand={handleExpandDrilldown}
                />
              ) : null}
            </>
          )}
        </div>
      )}
    </Panel>
  );
};

const App = () => {
  const [todosCentros, setTodosCentros] = useState([]);

  useEffect(() => {
    const buscarTodosCentros = async () => {
      try {
        const response = await fetch(END_POINT_PORTAL);

        if (!response.ok) throw new Error('Erro ao buscar centros de custo');
        const data = await response.json();
        setTodosCentros(data);
      } catch (error) {
        console.error(error);
      }
    };

    buscarTodosCentros();
  }, []);

  return (
    <div>
      {todosCentros.length === 0 && <p>Carregando visões de custo...</p>}
      {todosCentros.map(centro => (
        <ParametrosVisaoCusto key={centro.id} centroCusto={centro} />
      ))}
    </div>
  );
};

export {
  getTimeKeyFunction,
  handleExpandDrilldownFunction,
  handlePesquisarFunction,
  handleSelecionarCentroFunction,
  handleRemoverCentroFunction,
  handleSelecionarObjetoFunction,
  handleRemoverObjetoFunction,
  handleSelecionarIndicadorFunction,
  handleSelecionarDivisorFunction,
  loadCentrosFunction,
  loadObjetosFunction,
  loadIndicadoresFunction,
  loadDivisoresFunction
};
export default App;

function handleExpandDrilldownFunction(
  centrosSelecionados,
  objetosSelecionados,
  dataDe,
  dataAte,
  medida,
  indicadorSelecionado,
  divisorSelecionado
) {
  return async idSuperior => {
    try {
      const novaQuery = montarCubeQueryDrilldown({
        idsCentroCustoDetalhados: centrosSelecionados.map(c => c.id),
        idsObjetoCustoDetalhados: objetosSelecionados.map(c => c.id),
        dataInicio: dataDe,
        dataFim: dataAte,
        medida,
        indicadorSelecionado: indicadorSelecionado?.id || '',
        idSuperior: idSuperior,
        divisorSelecionado: divisorSelecionado?.id
      });

      const response = await fetch(
        `${API_BASE}/portal-transparencia-visao-load`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(novaQuery)
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar dados filhos');
      }

      const result = await response.json();
      return result.results?.[0]?.data;
    } catch (error) {
      console.error('Erro ao expandir drilldown:', error);
      return [];
    }
  };
}

function handlePesquisarFunction(
  centroCusto,
  centrosSelecionados,
  objetosSelecionados,
  dataDe,
  dataAte,
  medida,
  divisorSelecionado,
  periodo,
  indicadorSelecionado,
  id,
  tipoGrafico,
  setCentroCusto
) {
  return () => {
    let novaQuery;

    if (centroCusto.visaoCustoCubeDTO.tipoVisualizacao.codigo === 1) {
      novaQuery = montarCubeQueryDrilldown({
        idsCentroCustoDetalhados: centrosSelecionados.map(c => c.id),
        idsObjetoCustoDetalhados: objetosSelecionados.map(c => c.id),
        dataInicio: dataDe,
        dataFim: dataAte,
        medida,
        idSuperior: null,
        divisorSelecionado: divisorSelecionado?.id
      });
    } else {
      novaQuery = montarCubeQueryPadrao({
        medida,
        centrosSelecionados,
        objetosSelecionados,
        dataInicio: dataDe,
        dataFim: dataAte,
        periodo,
        indicadorSelecionado
      });
    }

    const payload = {
      id,
      dataDe,
      dataAte,
      periodo,
      tipoGrafico,
      centrosSelecionados: centrosSelecionados.map(c => c.id),
      objetosSelecionados: objetosSelecionados.map(o => o.id),
      indicadorSelecionado: indicadorSelecionado?.id || '',
      medida,
      cubeQuery: JSON.stringify(novaQuery)
    };

    fetch(END_POINT_PORTAL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(data => {
        if (centroCusto.visaoCustoCubeDTO.tipoVisualizacao.codigo === 1) {
          setCentroCusto(prev => {
            return {
              ...prev,
              cubeJsResponse: data?.length ? data[0]?.cubeJsResponse : []
            };
          });
        } else {
          setCentroCusto(prev => ({
            ...prev,
            cubeJsResponse: data?.length ? data[0]?.cubeJsResponse : []
          }));
        }
      })
      .catch(console.error);
  };
}

function getTimeKeyFunction() {
  return periodo => {
    switch (periodo) {
      case 'month':
        return 'DataBlendingVisao.data.month';
      case 'year':
        return 'DataBlendingVisao.data.year';
      case 'quarter':
        return 'DataBlendingVisao.data.quarter';
      default:
        return 'DataBlendingVisao.data';
    }
  };
}

function handleSelecionarDivisorFunction(setDivisorSelecionado) {
  return ({ value }) => {
    setDivisorSelecionado(value || null);
  };
}

function handleSelecionarIndicadorFunction(setIndicadorSelecionado) {
  return ({ value }) => {
    setIndicadorSelecionado(value || null);
  };
}

function handleRemoverObjetoFunction(
  setObjetosSelecionados,
  objetosSelecionados
) {
  return id => {
    setObjetosSelecionados(objetosSelecionados.filter(o => o.id !== id));
  };
}

function handleSelecionarObjetoFunction(
  objetosSelecionados,
  setObjetosSelecionados
) {
  return ({ value }) => {
    if (value && !objetosSelecionados.some(o => o.id === value?.id)) {
      setObjetosSelecionados([...objetosSelecionados, value]);
    }
  };
}

function handleRemoverCentroFunction(
  setCentrosSelecionados,
  centrosSelecionados
) {
  return id => {
    setCentrosSelecionados(centrosSelecionados.filter(c => c.id !== id));
  };
}

function handleSelecionarCentroFunction(
  centrosSelecionados,
  setCentrosSelecionados
) {
  return ({ value }) => {
    if (value && !centrosSelecionados.some(c => c.id == value?.id)) {
      setCentrosSelecionados([...centrosSelecionados, value]);
    }
  };
}

function loadDivisoresFunction() {
  return () => fetchOptions(`${API_BASE}/divisor`).then(data => data);
}

function loadIndicadoresFunction() {
  return () => fetchOptions(`${API_BASE}/indicador-oficial`).then(data => data);
}

function loadObjetosFunction(objetosSelecionados) {
  return () =>
    fetchOptions(
      `${API_BASE}/objetocusto`,
      objetosSelecionados.filter(c => c?.id).map(o => o.id)
    ).then(data => data);
}

function loadCentrosFunction(centrosSelecionados) {
  return () => {
    console.log(centrosSelecionados);
    return fetchOptions(
      `${API_BASE}/centrocusto`,
      centrosSelecionados.filter(c => c?.id).map(c => c.id),
      true
    ).then(data => data);
  };
}
